import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { LOGO_URL, euro } from "../lib/constants";
import {
    ChefHat,
    Wheat,
    BarChart3,
    PackageCheck,
    Truck,
    ShoppingBasket,
    Sparkles,
    ShieldCheck,
    Crown,
    ArrowRight,
    CheckCircle2,
    Star,
    BookOpen,
    Tag,
    Clock,
    Wand2,
    Utensils,
    Flame,
    Wine,
    Martini,
    GlassWater,
} from "lucide-react";

/**
 * Home introduttiva pubblica del portale.
 * Mostrata ai visitatori che arrivano su "/" senza essere autenticati.
 * Gli utenti già loggati vengono inoltrati alla dashboard.
 */
export default function Landing() {
    const { user, loading } = useAuth();
    const [info, setInfo] = useState(null);
    useEffect(() => {
        api.get("/public/landing-info")
            .then((r) => setInfo(r.data))
            .catch(() => setInfo(null));
    }, []);
    if (loading) return null;
    if (user) return <Navigate to="/dashboard" replace />;

    return (
        <div className="min-h-screen bg-brand-cream text-brand-ink">
            <TopBar />
            {info?.public_promo && <PromoStrip promo={info.public_promo} />}
            <Hero />
            <BeverageShowcase />
            <MagicChefShowcase />
            <Features />
            <HowItWorks />
            <Pricing info={info} />
            <FinalCta />
            <Footer />
        </div>
    );
}

function PromoStrip({ promo }) {
    const expires = promo.valid_until ? new Date(promo.valid_until) : null;
    return (
        <div
            className="bg-gradient-to-r from-brand-navy via-brand-navyLight to-brand-navy text-white py-2.5 px-4 text-center text-[13px] font-semibold flex items-center justify-center gap-2 flex-wrap"
            data-testid="landing-promo-strip"
        >
            <Sparkles size={14} className="text-brand-gold" />
            <span>
                {promo.description || `Sconto ${promo.discount_pct}%`} · sconto già applicato al checkout
                {promo.applies_to !== "all" && (
                    <> · solo piano <strong className="text-brand-gold">{promo.applies_to === "pro" ? "Pro" : promo.applies_to === "base" ? "Base" : "Top-up"}</strong></>
                )}
            </span>
            {expires && (
                <span className="inline-flex items-center gap-1 text-white/70 text-[11px]">
                    <Clock size={11} /> fino al {expires.toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}
                </span>
            )}
        </div>
    );
}

function TopBar() {
    return (
        <header className="sticky top-0 z-30 bg-brand-cream/85 backdrop-blur-md border-b border-brand-line">
            <div className="max-w-6xl mx-auto px-5 sm:px-8 py-3 flex items-center justify-between gap-3">
                <img
                    src={LOGO_URL}
                    alt="RENOVA360 Food Cost"
                    className="h-9 sm:h-10 w-auto object-contain"
                    data-testid="landing-logo"
                />
                <nav className="hidden md:flex items-center gap-7 text-[13px] font-semibold text-brand-ink/70">
                    <a href="#features" className="hover:text-brand-ink transition">Funzioni</a>
                    <a href="#magic-chef" className="inline-flex items-center gap-1 hover:text-brand-ink transition">
                        <Wand2 size={11} className="text-purple-600" />
                        <span className="bg-gradient-to-r from-purple-600 to-orange-500 bg-clip-text text-transparent font-bold">Magic Chef</span>
                    </a>
                    <a href="#howitworks" className="hover:text-brand-ink transition">Come funziona</a>
                    <a href="#pricing" className="hover:text-brand-ink transition">Prezzi</a>
                </nav>
                <Link
                    to="/login"
                    data-testid="landing-cta-login"
                    className="inline-flex items-center gap-1.5 rounded-full bg-brand-navy text-white px-4 sm:px-5 py-2 text-sm font-bold hover:bg-brand-navyLight transition"
                >
                    Accedi <ArrowRight size={14} />
                </Link>
            </div>
        </header>
    );
}

function Hero() {
    return (
        <section className="relative overflow-hidden">
            <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24 grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
                <div className="space-y-6">
                    <div className="eyebrow text-brand-gold flex items-center gap-2">
                        <Sparkles size={14} /> Per ristoratori italiani
                    </div>
                    <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-brand-navy">
                        Conosci davvero <span className="text-brand-gold">il costo</span> di ogni piatto.
                    </h1>
                    <p className="text-[17px] text-brand-ink/75 leading-relaxed max-w-xl">
                        Calcola in tempo reale il food cost dei tuoi piatti, monitora margini e
                        sprechi, e ordina dai fornitori al miglior prezzo. Tutto in un'unica
                        cabina di regia, costruita per la cucina italiana.
                    </p>
                    <div className="flex flex-wrap items-center gap-3 pt-2">
                        <Link
                            to="/login"
                            data-testid="landing-hero-cta"
                            className="inline-flex items-center gap-2 rounded-full bg-brand-gold text-brand-navy px-7 py-3 text-[15px] font-bold hover:bg-brand-goldDark transition shadow-gold"
                        >
                            Prova gratis 5 giorni <ArrowRight size={15} />
                        </Link>
                        <Link
                            to="/guide"
                            className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-paper text-brand-ink px-6 py-3 text-[14px] font-semibold hover:border-brand-gold/60 transition"
                        >
                            <BookOpen size={14} /> Vedi la guida
                        </Link>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pt-3 text-[12px] text-brand-muted">
                        <span className="inline-flex items-center gap-1.5">
                            <CheckCircle2 size={13} className="text-brand-gold" /> 253 ingredienti pre-caricati
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <CheckCircle2 size={13} className="text-brand-gold" /> 240+ ricette del catalogo
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <CheckCircle2 size={13} className="text-brand-gold" /> Nessuna carta richiesta
                        </span>
                    </div>
                </div>

                <DashboardPreview />
            </div>
            <DecorativeBlob />
        </section>
    );
}

function DashboardPreview() {
    return (
        <div className="relative">
            <div
                className="rounded-3xl shadow-card border border-brand-line overflow-hidden bg-paper rotate-[1deg] hover:rotate-0 transition-transform duration-500"
                data-testid="hero-preview"
            >
                <div className="bg-gradient-to-br from-brand-navy to-brand-navyLight px-5 py-5 text-white">
                    <div className="eyebrow text-brand-gold mb-2">Spaghetti alla Carbonara</div>
                    <div className="grid grid-cols-2 gap-3 text-[12px]">
                        <PreviewMetric label="Food cost" value="27%" tone="success" />
                        <PreviewMetric label="Margine" value="6,50 €" />
                        <PreviewMetric label="Costo/porz." value="2,02 €" />
                        <PreviewMetric label="Prezzo cons." value="9,00 €" tone="gold" />
                    </div>
                </div>
                <div className="px-5 py-4 space-y-2 text-[13px]">
                    <PreviewRow name="Spaghetti" qty="100 g" cost="0,30 €" />
                    <PreviewRow name="Guanciale" qty="50 g" cost="0,75 €" />
                    <PreviewRow name="Uova" qty="55 g" cost="0,35 €" />
                    <PreviewRow name="Pecorino" qty="25 g" cost="0,55 €" />
                    <PreviewRow name="Pepe nero" qty="2 g" cost="0,07 €" />
                </div>
            </div>
            <div className="absolute -top-4 -right-2 sm:right-4 rotate-[5deg]">
                <div className="bg-brand-gold text-brand-navy text-[10px] font-black uppercase tracking-[0.18em] px-3 py-1.5 rounded-full shadow-gold">
                    Food cost live
                </div>
            </div>
        </div>
    );
}

function PreviewMetric({ label, value, tone }) {
    const valueColor =
        tone === "success"
            ? "text-emerald-300"
            : tone === "gold"
                ? "text-brand-gold"
                : "text-white";
    return (
        <div className="rounded-lg bg-white/8 border border-white/10 px-3 py-2">
            <div className="text-[9px] uppercase tracking-[0.18em] text-white/55 font-bold">{label}</div>
            <div className={`font-display text-xl mt-0.5 ${valueColor}`}>{value}</div>
        </div>
    );
}

function PreviewRow({ name, qty, cost }) {
    return (
        <div className="flex items-center justify-between border-b border-brand-line/60 last:border-0 pb-1.5 last:pb-0">
            <div className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-gold" />
                <span className="text-brand-ink">{name}</span>
            </div>
            <div className="text-brand-muted">{qty}</div>
            <div className="font-semibold text-brand-ink">{cost}</div>
        </div>
    );
}

function DecorativeBlob() {
    return (
        <>
            <div className="pointer-events-none absolute -top-20 -right-20 h-72 w-72 rounded-full bg-brand-gold/10 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-32 -left-20 h-96 w-96 rounded-full bg-brand-navy/8 blur-3xl" />
        </>
    );
}

function Features() {
    const items = [
        {
            icon: Wheat,
            title: "Dispensa intelligente",
            desc: "253 ingredienti italiani già pronti, con scarto, costo medio ponderato e auto-conversione delle unità.",
        },
        {
            icon: ChefHat,
            title: "Ricette con food cost live",
            desc: "240+ ricette del catalogo importabili, calcolo automatico costo/porzione e prezzo suggerito al 30%.",
        },
        {
            icon: Sparkles,
            title: "AI per fatture e menu",
            desc: "Carica DDT/fatture o un menu PDF: l'AI estrae prodotti, prezzi e ingredienti in pochi secondi.",
        },
        {
            icon: PackageCheck,
            title: "Ordini smart multi-fornitore",
            desc: "Il carrello sceglie da solo il miglior prezzo riga per riga. Stampa, copia, manda.",
        },
        {
            icon: BarChart3,
            title: "Statistiche operative",
            desc: "Food cost distribuiti per piatto, spesa per categoria, top fornitori: i KPI che servono in cucina.",
        },
        {
            icon: ShieldCheck,
            title: "Sicurezza enterprise",
            desc: "Login Google, dati isolati per ristorante, pagamenti Stripe PCI-DSS. Niente password da gestire.",
        },
    ];
    return (
        <section id="features" className="py-20 bg-paper border-y border-brand-line">
            <div className="max-w-6xl mx-auto px-5 sm:px-8">
                <div className="text-center mb-12 max-w-2xl mx-auto">
                    <div className="eyebrow text-brand-gold mb-2">Tutto in un posto</div>
                    <h2 className="font-display text-4xl sm:text-5xl text-brand-navy">
                        Una cabina di regia per la tua cucina
                    </h2>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {items.map(({ icon: Icon, title, desc }) => (
                        <div
                            key={title}
                            className="rounded-2xl border border-brand-line bg-brand-cream p-6 hover:border-brand-gold/40 transition group"
                        >
                            <div className="h-11 w-11 rounded-full bg-brand-gold/15 text-brand-gold flex items-center justify-center mb-4 group-hover:bg-brand-gold/25 transition">
                                <Icon size={20} />
                            </div>
                            <h3 className="font-display text-xl text-brand-ink mb-1.5">{title}</h3>
                            <p className="text-[13.5px] text-brand-ink/75 leading-relaxed">{desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function HowItWorks() {
    const steps = [
        {
            n: 1,
            title: "Accedi con Google",
            desc: "Un solo click. Niente form da compilare, niente password. 5 giorni di Pro inclusi al primo accesso.",
        },
        {
            n: 2,
            title: "Carica il tuo menu",
            desc: "Foto o PDF del menu attuale: l'AI estrae piatti, prezzi e ingredienti. Oppure parti dal catalogo di 240+ ricette.",
        },
        {
            n: 3,
            title: "Allinea prezzi e fornitori",
            desc: "Carica fatture in foto: l'OCR aggiorna costi e media ponderata. Configura i fornitori con i loro listini.",
        },
        {
            n: 4,
            title: "Ottimizza margini ogni giorno",
            desc: "Dashboard, alert sui piatti rossi, ordini smart che scelgono il fornitore più conveniente.",
        },
    ];
    return (
        <section id="howitworks" className="py-20">
            <div className="max-w-5xl mx-auto px-5 sm:px-8">
                <div className="text-center mb-12">
                    <div className="eyebrow text-brand-gold mb-2">In 4 passi</div>
                    <h2 className="font-display text-4xl sm:text-5xl text-brand-navy">
                        Come funziona
                    </h2>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                    {steps.map((s) => (
                        <div
                            key={s.n}
                            className="rounded-2xl bg-paper border border-brand-line p-6 flex gap-5"
                        >
                            <div className="font-display text-5xl text-brand-gold/40 leading-none">
                                {s.n.toString().padStart(2, "0")}
                            </div>
                            <div>
                                <h3 className="font-display text-xl text-brand-ink mb-1">{s.title}</h3>
                                <p className="text-[13.5px] text-brand-ink/75 leading-relaxed">{s.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function BeverageShowcase() {
    const cocktails = [
        { name: "Negroni", family: "Aperitivo IBA", ml: "30+30+30", abv: "24°" },
        { name: "Aperol Spritz", family: "Aperitivo IT", ml: "60+90+30", abv: "8°" },
        { name: "Espresso Martini", family: "After dinner", ml: "40+15+30", abv: "20°" },
        { name: "Mojito", family: "Long drink", ml: "45+30+80", abv: "9°" },
    ];
    return (
        <section
            id="beverage-cost"
            className="relative py-20 overflow-hidden"
            data-testid="landing-beverage-section"
        >
            {/* Bar atmosphere background */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#0f172a] via-[#1a2a44] to-[#3f1d38] pointer-events-none" />
            <div className="absolute top-10 left-1/3 h-72 w-72 rounded-full bg-amber-400/15 blur-3xl pointer-events-none" />
            <div className="absolute bottom-12 right-12 h-80 w-80 rounded-full bg-rose-500/10 blur-3xl pointer-events-none" />
            <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
                backgroundImage: "radial-gradient(circle at 25% 25%, rgba(255,255,255,0.4) 1px, transparent 1px), radial-gradient(circle at 75% 75%, rgba(255,255,255,0.3) 1px, transparent 1px)",
                backgroundSize: "60px 60px",
            }} />

            <div className="relative max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 items-center">
                <div>
                    <div className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500 text-brand-navy px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] shadow-lg">
                        <Sparkles size={11} /> Novità · Beverage Cost
                    </div>
                    <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-white">
                        <span className="bg-gradient-to-r from-amber-300 via-orange-400 to-rose-400 bg-clip-text text-transparent">
                            Magic Bartender
                        </span>{" "}
                        — quanto costa il bar del tuo evento?
                    </h2>
                    <p className="mt-5 text-[16.5px] text-white/85 leading-relaxed max-w-xl">
                        Più di <strong className="text-amber-300">200 cocktail con ricetta</strong> (IBA Official, Italian Classics, Modern),
                        listino bevande personalizzato e calcolo automatico dei costi per feste, eventi e ristoranti.
                        Sceglie da solo il <strong className="text-amber-300">miglior fornitore</strong> e genera l'ordine in bozza.
                    </p>
                    <ul className="mt-6 space-y-2.5">
                        {[
                            { icon: <BookOpen size={14} />, text: "200+ ricette pronte con istruzioni passo-passo (Negroni, Spritz, Mojito, Old Fashioned…)" },
                            { icon: <Wine size={14} />, text: "Carica il tuo listino bevande in CSV (gin, vermouth, mixer, vino, birra…)" },
                            { icon: <Sparkles size={14} />, text: "Magic Bartender: scegli cocktail + persone → costi a colpo d'occhio" },
                            { icon: <PackageCheck size={14} />, text: "Bozza ordine smart: divide automaticamente per fornitore migliore" },
                        ].map((row, i) => (
                            <li key={i} className="flex items-start gap-2.5 text-[14px] text-white/90">
                                <div className="h-6 w-6 rounded-full bg-amber-400/15 border border-amber-400/30 text-amber-300 flex items-center justify-center shrink-0 mt-0.5">
                                    {row.icon}
                                </div>
                                <span>{row.text}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-7 flex flex-wrap items-center gap-3">
                        <Link
                            to="/login"
                            data-testid="landing-beverage-cta"
                            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500 text-brand-navy px-7 py-3 text-[15px] font-bold hover:shadow-2xl transition shadow-lg"
                        >
                            <Martini size={15} /> Prova Magic Bartender
                        </Link>
                        <span className="text-[12px] text-white/60">
                            Per bar, ristoranti, catering, eventi e feste private
                        </span>
                    </div>
                </div>

                {/* Mock cocktail menu card */}
                <div className="relative">
                    <div className="rounded-3xl bg-white/95 backdrop-blur border border-amber-400/30 shadow-2xl p-6 transform lg:-rotate-1 hover:rotate-0 transition-transform duration-500">
                        <div className="flex items-center gap-3 pb-3 border-b border-brand-line/60 mb-4">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-amber-400 to-rose-500 text-white flex items-center justify-center shadow">
                                <Martini size={18} />
                            </div>
                            <div>
                                <div className="font-display text-lg text-brand-ink leading-tight">Magic Bartender</div>
                                <div className="text-[11px] text-brand-muted">Evento 50 persone · 100 drink</div>
                            </div>
                            <span className="ml-auto text-[10px] uppercase tracking-wider font-bold text-rose-600">Calcolo · LIVE</span>
                        </div>
                        <div className="space-y-2.5">
                            {cocktails.map((c, i) => (
                                <div key={i} className="rounded-xl bg-gradient-to-r from-brand-cream/80 to-amber-50/60 border border-brand-line/60 px-4 py-2.5 flex items-center gap-3">
                                    <div className="h-8 w-8 rounded-full bg-white border border-amber-300/40 flex items-center justify-center text-amber-700 shrink-0">
                                        <GlassWater size={14} />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="font-display text-[14px] text-brand-ink truncate">{c.name}</div>
                                        <div className="text-[10px] text-brand-muted">{c.family} · {c.ml} ml · {c.abv}</div>
                                    </div>
                                    <div className="text-[11px] font-bold text-brand-gold whitespace-nowrap">25× drink</div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-4 pt-3 border-t border-brand-line/60 grid grid-cols-3 gap-2">
                            <div className="text-center">
                                <div className="text-[9px] uppercase tracking-wider text-brand-muted font-bold">Totale</div>
                                <div className="font-display text-base text-brand-navy">€ 187,40</div>
                            </div>
                            <div className="text-center border-x border-brand-line/60">
                                <div className="text-[9px] uppercase tracking-wider text-brand-muted font-bold">A persona</div>
                                <div className="font-display text-base text-brand-gold">€ 3,75</div>
                            </div>
                            <div className="text-center">
                                <div className="text-[9px] uppercase tracking-wider text-brand-muted font-bold">Fornitori</div>
                                <div className="font-display text-base text-brand-navy">2</div>
                            </div>
                        </div>
                    </div>
                    <div className="absolute -bottom-4 -right-4 h-14 w-14 rounded-full bg-gradient-to-br from-amber-400 to-rose-500 text-white flex items-center justify-center shadow-xl rotate-12">
                        <Wine size={22} />
                    </div>
                </div>
            </div>
        </section>
    );
}


function MagicChefShowcase() {
    return (
        <section id="magic-chef" className="relative py-20 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-pink-50/40 to-orange-50/40 pointer-events-none" />
            <div className="absolute top-12 right-12 h-72 w-72 rounded-full bg-purple-300/20 blur-3xl pointer-events-none" />
            <div className="absolute bottom-8 left-8 h-64 w-64 rounded-full bg-orange-300/20 blur-3xl pointer-events-none" />
            <div className="relative max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[0.95fr_1.05fr] gap-12 items-center">
                <div>
                    <div className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 text-white px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] shadow">
                        <Wand2 size={11} /> Novità · AI
                    </div>
                    <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-brand-navy">
                        <span className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 bg-clip-text text-transparent">
                            Magic Chef
                        </span>{" "}
                        — dimmi cosa hai in cucina e ti creo il menu.
                    </h2>
                    <p className="mt-5 text-[16.5px] text-brand-ink/80 leading-relaxed max-w-xl">
                        Inserisci gli ingredienti che hai oggi (e quanti ne hai). In pochi secondi
                        l'AI genera un menu coerente di <strong>3-6 piatti</strong> con dosi,
                        procedimento, tempi, allergeni e prezzo di vendita consigliato.
                    </p>
                    <ul className="mt-6 space-y-2.5">
                        {[
                            { icon: <Utensils size={14} />, text: "Crea i Piatti del Giorno in 2 click e stampi il PDF A4 per la sala" },
                            { icon: <Flame size={14} />, text: "Allergeni e prezzi suggeriti già pronti — modificabili al volo" },
                            { icon: <ChefHat size={14} />, text: "Cronologia personale: ritrovi tutti i menu generati" },
                            { icon: <Sparkles size={14} />, text: "Incluso nel piano Base (2/mese) e Pro (5/mese)" },
                        ].map((row, i) => (
                            <li key={i} className="flex items-start gap-2.5 text-[14px] text-brand-ink/85">
                                <div className="h-6 w-6 rounded-full bg-white border border-brand-line text-brand-navy flex items-center justify-center shrink-0 mt-0.5">
                                    {row.icon}
                                </div>
                                <span>{row.text}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-7 flex flex-wrap items-center gap-3">
                        <Link
                            to="/login"
                            data-testid="landing-magic-cta"
                            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 text-white px-7 py-3 text-[15px] font-bold hover:shadow-2xl transition shadow-lg"
                        >
                            <Wand2 size={15} /> Prova Magic Chef gratis
                        </Link>
                        <span className="text-[12px] text-brand-muted">
                            Compreso nei 5 giorni di prova Pro
                        </span>
                    </div>
                </div>

                {/* Mock card simulazione dialog AI */}
                <div className="relative">
                    <div className="rounded-3xl bg-white/95 backdrop-blur border border-purple-200/50 shadow-2xl p-7 transform lg:rotate-1 hover:rotate-0 transition-transform duration-500">
                        <div className="flex items-center gap-3 pb-3 border-b border-brand-line/60 mb-4">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-purple-600 to-orange-500 text-white flex items-center justify-center shadow">
                                <ChefHat size={18} />
                            </div>
                            <div>
                                <div className="font-display text-lg text-brand-ink leading-tight">Magic Chef</div>
                                <div className="text-[11px] text-brand-muted">Sinfonia Mediterranea</div>
                            </div>
                            <span className="ml-auto text-[10px] uppercase tracking-wider font-bold text-purple-600">AI · LIVE</span>
                        </div>
                        <div className="space-y-3">
                            {[
                                { course: "ANTIPASTO", name: "Bruschetta al pomodoro fresco", price: "€ 6,50", time: "10 min" },
                                { course: "PRIMO", name: "Spaghetti pomodoro e basilico", price: "€ 11,00", time: "20 min" },
                                { course: "SECONDO", name: "Mozzarella in carrozza dorata", price: "€ 12,50", time: "25 min" },
                            ].map((d, i) => (
                                <div key={i} className="rounded-xl bg-brand-cream/60 border border-brand-line/60 px-4 py-3">
                                    <div className="text-[9.5px] uppercase tracking-[0.18em] text-brand-gold font-bold">{d.course}</div>
                                    <div className="mt-0.5 flex items-center justify-between gap-3">
                                        <span className="font-display text-[15px] text-brand-ink truncate">{d.name}</span>
                                        <span className="font-display text-base text-brand-gold whitespace-nowrap">{d.price}</span>
                                    </div>
                                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-brand-muted">
                                        <Clock size={9} /> {d.time}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-4 pt-3 border-t border-brand-line/60 flex items-center justify-between text-[10px] text-brand-muted">
                            <span className="inline-flex items-center gap-1">
                                <Utensils size={10} /> 3 piatti · 4 persone
                            </span>
                            <span className="inline-flex items-center gap-1 text-purple-700 font-bold">
                                <Sparkles size={10} /> Generato in 6,2s
                            </span>
                        </div>
                    </div>
                    <div className="absolute -bottom-4 -left-4 h-14 w-14 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 text-white flex items-center justify-center shadow-xl rotate-[-12deg]">
                        <Wand2 size={22} />
                    </div>
                </div>
            </div>
        </section>
    );
}

function Pricing({ info }) {
    const plans = info?.plans || [];
    const base = plans.find((p) => p.id === "monthly_base_29");
    const pro = plans.find((p) => p.id === "monthly_pro_49");
    const topup = plans.find((p) => p.id === "topup_100_uploads");
    return (
        <section id="pricing" className="py-20 bg-paper border-y border-brand-line">
            <div className="max-w-5xl mx-auto px-5 sm:px-8">
                <div className="text-center mb-10 max-w-2xl mx-auto">
                    <div className="eyebrow text-brand-gold mb-2">Trasparenza</div>
                    <h2 className="font-display text-4xl sm:text-5xl text-brand-navy">
                        Piani semplici, senza sorprese
                    </h2>
                    <p className="mt-3 text-[14px] text-brand-ink/75">
                        Inizia con {info?.trial_days_total ?? 5} giorni di Pro gratis. Disdici quando vuoi.
                    </p>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                    <PriceCard
                        name="Base"
                        amount={base?.amount ?? 29}
                        autoPromo={base?.auto_promo}
                        bullets={[
                            "Ingredienti, ricette, acquisti illimitati",
                            "30 fatture OCR / mese",
                            "2 import menu PDF / mese",
                            "5 ricette AI / mese",
                        ]}
                    />
                    <PriceCard
                        name="Pro"
                        amount={pro?.amount ?? 49}
                        autoPromo={pro?.auto_promo}
                        recommended
                        bullets={[
                            "Tutto di Base, in più…",
                            "Ordini intelligenti multi-fornitore",
                            "60 fatture OCR / mese",
                            "5 import menu PDF / mese",
                            "15 ricette AI / mese",
                        ]}
                    />
                </div>
                <p className="mt-6 text-center text-[12px] text-brand-muted" data-testid="landing-topup-line">
                    Ricarica una tantum{" "}
                    {topup?.auto_promo ? (
                        <>
                            <span className="line-through text-brand-muted/70">{euro(topup.amount)}</span>{" "}
                            <span className="font-bold text-emerald-700">{euro(topup.auto_promo.discounted_amount)}</span>
                        </>
                    ) : (
                        <strong>{euro(topup?.amount ?? 25)}</strong>
                    )}
                    {" "}disponibile: +80 fatture, +5 menu, +15 ricette AI, +10 Magic Menu. Crediti che non scadono.
                </p>
            </div>
        </section>
    );
}

function PriceCard({ name, amount, autoPromo, bullets, recommended }) {
    return (
        <div
            className={`relative rounded-2xl p-7 ${
                recommended
                    ? "bg-brand-navy text-white border border-brand-gold shadow-[0_24px_60px_rgba(26,42,68,0.18)]"
                    : "bg-brand-cream border border-brand-line"
            }`}
            data-testid={`price-card-${name.toLowerCase()}`}
        >
            {recommended && (
                <div className="absolute -top-3 left-7 inline-flex items-center gap-1 rounded-full bg-brand-gold text-brand-navy text-[10px] font-black uppercase tracking-[0.18em] px-3 py-1">
                    <Crown size={11} /> Consigliato
                </div>
            )}
            <div className="font-display text-3xl">{name}</div>
            {autoPromo ? (
                <div className="mt-2">
                    <div className="flex items-baseline gap-2 flex-wrap">
                        <span
                            className={`font-display text-2xl line-through decoration-2 ${
                                recommended ? "text-white/60 decoration-brand-gold/60" : "text-brand-muted decoration-brand-danger/60"
                            }`}
                        >
                            {euro(amount)}
                        </span>
                        <span
                            className={`font-display text-5xl ${recommended ? "text-brand-gold" : "text-emerald-700"}`}
                            data-testid={`price-discounted-${name.toLowerCase()}`}
                        >
                            {euro(autoPromo.discounted_amount)}
                        </span>
                        <span className={`text-sm ${recommended ? "text-white/70" : "text-brand-muted"}`}>/ mese</span>
                    </div>
                    <div className="mt-2">
                        <span
                            className={`inline-flex items-center gap-1 rounded-full text-[10px] font-bold px-2 py-0.5 ${
                                recommended ? "bg-brand-gold text-brand-navy" : "bg-emerald-50 border border-emerald-200 text-emerald-700"
                            }`}
                        >
                            <Tag size={10} /> -{autoPromo.discount_pct}% applicato
                        </span>
                    </div>
                </div>
            ) : (
                <div className="mt-2 flex items-baseline gap-1.5">
                    <span className="font-display text-5xl">{euro(amount)}</span>
                    <span className={`text-sm ${recommended ? "text-white/70" : "text-brand-muted"}`}>/ mese</span>
                </div>
            )}
            <ul className="mt-5 space-y-2">
                {bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-[13.5px]">
                        <CheckCircle2 size={15} className={recommended ? "text-brand-gold mt-0.5" : "text-brand-navy mt-0.5"} />
                        <span className={recommended ? "text-white/90" : "text-brand-ink/85"}>{b}</span>
                    </li>
                ))}
            </ul>
            <Link
                to="/login"
                className={`mt-6 block text-center rounded-full px-5 py-3 text-sm font-bold transition ${
                    recommended
                        ? "bg-brand-gold text-brand-navy hover:bg-brand-goldDark"
                        : "bg-brand-navy text-white hover:bg-brand-navyLight"
                }`}
            >
                Inizia ora
            </Link>
        </div>
    );
}

function FinalCta() {
    return (
        <section className="py-20">
            <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
                <div className="flex justify-center mb-4">
                    <Star className="text-brand-gold" size={28} />
                </div>
                <h2 className="font-display text-4xl sm:text-5xl text-brand-navy leading-tight">
                    Sotto controllo, da oggi.
                </h2>
                <p className="mt-4 text-[15px] text-brand-ink/75 max-w-2xl mx-auto">
                    Smetti di stimare il food cost a mente. RENOVA360 fa i conti al posto tuo,
                    ti avvisa quando un piatto sale fuori linea, e ottimizza ogni ordine.
                </p>
                <Link
                    to="/login"
                    data-testid="landing-final-cta"
                    className="mt-7 inline-flex items-center gap-2 rounded-full bg-brand-gold text-brand-navy px-8 py-3.5 text-[15px] font-black hover:bg-brand-goldDark transition shadow-gold"
                >
                    Inizia gratis <ArrowRight size={16} />
                </Link>
            </div>
        </section>
    );
}

function Footer() {
    return (
        <footer className="border-t border-brand-line bg-paper">
            <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-[12px] text-brand-muted">
                <div className="flex items-center gap-3">
                    <span className="font-semibold text-brand-ink">RENOVA360</span>
                    <span>Food Cost Manager</span>
                </div>
                <nav className="flex items-center gap-5">
                    <Link to="/guide" className="hover:text-brand-ink transition">Guida</Link>
                    <Link to="/privacy-policy" className="hover:text-brand-ink transition">Privacy</Link>
                    <Link to="/cookie-policy" className="hover:text-brand-ink transition">Cookie</Link>
                </nav>
            </div>
        </footer>
    );
}
